import { Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { CreateTopicPayload, Message, Thread } from '../message-center.model';
import { MatFormField, MatHint, MatInput } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MessageCenterService } from '@k12/messaging';
import { isSameAccountToken } from '@k12/app-services';
import { MsalService } from '@azure/msal-angular';
import { debounceTime } from 'rxjs';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'lib-message-center-chat-form, [chat-form]',
  imports: [
    MatFormField,
    MatIcon,
    FormsModule,
    MatButtonModule,
    MatTooltipModule,
    MatInput,
    MatHint,
    ReactiveFormsModule
  ],
  templateUrl: './message-center-chat-form.component.html',
  styleUrl: './message-center-chat-form.component.scss'
})
export class MessageCenterChatFormComponent implements OnChanges {
  private readonly messageService: MessageCenterService = inject(MessageCenterService);
  private readonly msalService: MsalService = inject(MsalService);
  private readonly formBuilder: FormBuilder = inject(FormBuilder);

  @Input() thread: Thread | null = null;
  @Input() createTopicPayload: CreateTopicPayload | null = null;

  @Output() messageChanged = new EventEmitter<string>();
  @Output() sendMessage = new EventEmitter<Message>();
  @Output() scrollDown = new EventEmitter<void>();
  @Output() newThreadCreated = new EventEmitter<Thread>();

  private readonly accountId: string | null = null;

  public form: FormGroup;
  public isSendingMessage: boolean = false;

  constructor() {
    this.accountId = this.msalService.instance.getActiveAccount()?.localAccountId ?? null;
    this.form = this.formBuilder.group({
      message: ['', Validators.required]
    });
    // register form change events with a simple debouncing
    this.form.valueChanges.pipe(debounceTime(100)).subscribe((value) => {
      this.messageChanged.emit(value.message);
    });
  }

  public createNewTopic(callback?: (res: Thread) => void): void {
    if (this.thread == null || this.createTopicPayload == null) {
      alert('Please select new message category');
      return;
    }
    this.messageService.CreateAccountMessageTopic(this.createTopicPayload).subscribe((res: Thread) => {
      if (callback) {
        callback(res);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['thread']) {
      const previousThread = changes['thread'].previousValue as Thread | null;
      const currentThread = changes['thread'].currentValue as Thread | null;
      
      // Only reset if the thread ID actually changed (not just the reference)
      if (previousThread?.id !== currentThread?.id) {
        queueMicrotask(() => this.resetForm());
      }
    }
  }

  public handleScrollDown(): void {
    this.scrollDown.emit();
  }

  public handleSendMessage(): void {
    if (this.thread === null) return;
    this.isSendingMessage = true;
    if (this.thread.id == 'new') {
      // create the topic before we send the message
      this.createNewTopic((topic: Thread) => {
        this.thread = { ...this.thread, ...topic };
        if (!this.thread.messages) {
          this.thread.messages = [];
        }
        this.sendMessageHelper(this.message, () => {
          if (this.thread) {
            this.newThreadCreated.emit(this.thread);
          }
          this.resetForm();
        });
      });
    } else {
      this.sendMessageHelper(this.message, () => {
        this.resetForm();
      });
    }
  }

  public get message(): string {
    return this.form.controls['message']?.value ?? '';
  }

  private sendMessageHelper(messageText: string, callback?: () => void): void {
    if (this.thread === null) return;
    const senderIsAssignedToThread = isSameAccountToken(this.accountId, this.thread.assignedToId);
    const payload = {
      message: messageText,
      senderId: this.accountId ?? null,
      accountMessageTopicId: this.thread.id,
      recipientId: senderIsAssignedToThread ? this.thread.participantId : this.thread.assignedToId
    };
    this.messageService.SendAccountMessage(payload).subscribe((res) => {
      if (this.thread) {
        res.senderId = this.accountId ?? undefined;
        this.thread.messages.push(res);
      }
      this.sendMessage.emit(res);
      if (callback) {
        callback();
      }
    });
  }

  private resetForm(): void {
    this.isSendingMessage = false;
    this.form.patchValue({ message: '' }, { emitEvent: true });
  }
}
